import {
  Button,
  CheckBox,
  Icon,
  Link,
  LoadingSpinner,
  Pill,
  Popover,
  PopoverContent,
} from '@idexx-laboratories/spot-react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { GridRenderer, GridRendererProps } from '../../components/Grid/Grid';
import QualificationIcon from '../../components/QualificationIcon/QualificationIcon';
import TableCellTextBox from '../../components/TableCellTextBox/TableCellTextBox';
import { useStore } from '../../hooks/use-store';
import { useCheckSinglePermission } from '../../security/security-hook';
import {
  AgentData,
  AgentQualifications,
  AuditStatus,
  UpgradeStatus,
} from '../../types/practice-types';
import {
  CHECK_QUALIFICATIONS_QUALIFICATION_OVERRIDE,
  PERMISSION_VIEW,
} from '../../types/users-types';
import { formatMBytes, isDefined } from '../../utils/object-utils';
import './CheckQualifications.scss';

const DETAIL_DOUBLE_CLICK = 2;

export const AllRenderer = observer((props: GridRendererProps<AgentData>): React.JSX.Element => {
  const { row, index, tableId } = props;
  return (
    <CheckBox
      id={`${tableId}-all_${index}`}
      aria-label='Select Agent'
      checked={row.checked || false}
      disabled={row.auditStatus === AuditStatus.PENDING}
      onChange={() => runInAction(() => (row.checked = !row.checked))}
      data-testid={`select-${row.agentId}-cb`}
    />
  );
});

export const StatusRenderer = observer((props: GridRendererProps<AgentData>) => {
  switch (props.row.status) {
    case UpgradeStatus.UPGRADE:
      return <>Upgrade</>;
    case UpgradeStatus.DO_NOT_UPGRADE:
      return <>Do Not Upgrade</>;
    case UpgradeStatus.UPGRADE_MANUALLY_APPROVED:
      return <>Upgrade (Manually Approved)</>;
  }
  return null;
});

export const LabelRenderer = observer((props: GridRendererProps<AgentData>) => {
  const store = useStore().checkQualificationsStore;
  const { row, index } = props;

  const clickHandler = (e: any) => {
    // on double click only
    if (store.editLabelIndex === null && e.detail === DETAIL_DOUBLE_CLICK) {
      runInAction(() => {
        if (row.label === undefined) row.label = '';
        store.editLabelIndex = index;
      });
    }
  };

  return index === store.editLabelIndex ? (
    <TableCellTextBox
      id={`agent-label-${row.agentId}`}
      ariaLabel='label-editor'
      placeholder='Add a nickname'
      initialValue={row.label}
      focus
      maxLength={15}
      disabled={store.loaders.label}
      onSave={v => void store.updateLabel(v)}
      onCancel={() => runInAction(() => (store.editLabelIndex = null))}
      data-testid={`label-${row.agentId}-input`}
    />
  ) : (
    <span
      role='label-display'
      className={`label-cell ${row.label ? '' : 'gray-span'}`}
      onClick={e => clickHandler(e)}
      data-testid={`label-${row.agentId}-span`}
    >
      {row.label || 'Add a nickname'}
    </span>
  );
});

export const AuditRenderer = observer((props: GridRendererProps<AgentData>) => {
  const { row } = props;
  const store = useStore().checkQualificationsStore;
  if (row.auditLoading) return <LoadingSpinner />;

  if (
    store.duplicateAgentsToUpgradeNames.includes(row.name) &&
    row.auditStatus !== AuditStatus.PENDING
  )
    return (
      <Button
        style={{ padding: 0 }}
        variant='link'
        size='small'
        title='Click to refresh the computer name'
        onClick={() => void store.initiateCheck(row)}
        data-testid={`refresh-name-${row.agentId}-btn`}
      >
        Refresh Name
      </Button>
    );

  switch (row.auditStatus) {
    case AuditStatus.QUALIFIED:
      return (
        <Pill
          color='positive'
          size='small'
          variant='outlined'
          iconLeft='checkmark'
          label='Qualified'
        />
      );
    case AuditStatus.UNQUALIFIED:
      return (
        <Pill
          color='negative'
          size='small'
          variant='outlined'
          iconLeft='cancel'
          label='Unqualified'
        />
      );
    case AuditStatus.REQUIRED:
      return (
        <Button
          style={{ padding: 0 }}
          variant='link'
          size='small'
          title='Click to check if this computer meets upgrade requirements'
          onClick={() => void store.initiateCheck(row)}
          data-testid={`initiate-check-${row.agentId}-btn`}
        >
          Initiate Check
        </Button>
      );
    case AuditStatus.ERROR: {
      const warningTooltip = `
        1. Confirm computer is powered on and connected to the internet
        2. Restart the computer
        3. Check for any pending Windows updates and install if present
        4. Contact Cornerstone Customer Support if problems persist
        `.replaceAll(/\r?\n\s+/g, '\n');
      return (
        <Pill
          color='warning'
          size='small'
          variant='outlined'
          interactive
          iconLeft='alert-notification'
          title={warningTooltip}
          label='Check Failed'
        />
      );
    }
    case AuditStatus.PENDING:
      return store.loaders.pending ? <LoadingSpinner /> : <>Processing...</>;
    default:
      return null;
  }
});

interface ActionsType extends GridRendererProps<AgentData> {
  action: (row: any, index: any) => void;
}

const ExpandCollapseRenderer = observer((props: ActionsType) => {
  const { row, action, expandedRowIds, index } = props;
  const iconPath = expandedRowIds?.includes(row.agentId) ? 'caret-up' : 'caret-down';
  const iconHint = expandedRowIds?.includes(row.agentId) ? 'Collapse' : 'Expand';
  return (
    <div className='actions-cell'>
      <Icon
        role='expand-collapse-icon'
        className='expand-collapse-icon'
        iconName={iconPath}
        aria-label={`Agent ${iconHint}`}
        title={iconHint}
        onClick={() => action(row, index)}
        data-testid={`expand-${row.agentId}-row`}
      />
    </div>
  );
});

const nonOverridableQualificationsFields: (keyof AgentQualifications)[] = [
  'csVersion',
  'cDiskFreeSpace',
  'os',
  'connectivity',
  'powershell',
];

export const hasOverridableQualificationIssue = (agent: AgentData): boolean =>
  agent.auditStatus === AuditStatus.UNQUALIFIED &&
  isDefined(agent.qualifications) &&
  nonOverridableQualificationsFields.every(field => agent.qualifications?.[field]) &&
  (Object.keys(agent.qualifications) as (keyof AgentQualifications)[])
    .filter(key => !agent.qualifications?.[key])
    .some(field => !nonOverridableQualificationsFields.includes(field));

const overrideAvailable = (agent: AgentData): boolean =>
  agent.auditStatus === AuditStatus.UNQUALIFIED &&
  agent.status !== UpgradeStatus.UPGRADE_MANUALLY_APPROVED &&
  isDefined(agent.qualifications) &&
  nonOverridableQualificationsFields.every(field => agent.qualifications?.[field]);

export const ActionsRenderer = observer((props: GridRendererProps<AgentData>) => {
  const { row, index } = props;
  const store = useStore().checkQualificationsStore;
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const isAdminOverride = useCheckSinglePermission(
    CHECK_QUALIFICATIONS_QUALIFICATION_OVERRIDE,
    PERMISSION_VIEW,
  );

  const upgradeItemDisabled =
    row.type === 'Server' && row.status === UpgradeStatus.UPGRADE && store.serversToUpgrade <= 1;

  return (
    <div className='actions-cell'>
      <Popover
        side='bottom'
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        data-testid={`actions-${row.agentId}-popover`}
        content={
          <PopoverContent size='small'>
            <Button
              variant='link'
              size='small'
              style={{ display: 'block' }}
              onClick={() =>
                runInAction(() => {
                  setIsOpen(false);
                  store.editLabelIndex = index;
                })
              }
              data-testid={`edit-label-${row.agentId}-link`}
            >
              Add/Edit Nickname
            </Button>
            <Button
              variant='link'
              size='small'
              style={{ display: 'block' }}
              disabled={
                row.auditStatus === AuditStatus.PENDING || row.rowLoading || row.auditLoading
              }
              onClick={() => {
                void store.initiateCheck(row);
                setIsOpen(false);
              }}
              aria-label='Agent Initiate Check'
              data-testid={`initiate-check-${row.agentId}-link`}
            >
              Initiate Check
            </Button>
            <Button
              variant='link'
              size='small'
              style={{ display: 'block' }}
              disabled={
                row.auditStatus === AuditStatus.PENDING || row.rowLoading || row.auditLoading
              }
              onClick={() => {
                void store.sendIdentificationAlert(index);
                setIsOpen(false);
              }}
              data-testid={`find-me-${row.agentId}-link`}
            >
              Find me
            </Button>
            <Button
              variant='link'
              size='small'
              style={{ display: 'block' }}
              disabled={store.loaders.status || upgradeItemDisabled}
              onClick={() => {
                store.switchStatus(index);
                setIsOpen(false);
              }}
              data-testid={`status-${row.status}-${row.agentId}-link`}
            >
              {row.status === UpgradeStatus.DO_NOT_UPGRADE ? 'Upgrade' : 'Do Not Upgrade'}
            </Button>
            <Button
              variant='link'
              size='small'
              style={{ display: 'block' }}
              disabled={store.loaders.status || !overrideAvailable(row)}
              onClick={() => {
                store.switchStatusToOverride(index);
                setIsOpen(false);
              }}
              data-testid={`qualification-override-${row.agentId}-link`}
            >
              {isAdminOverride ? 'Qualification Override' : 'Override Failed Qualification'}
            </Button>
          </PopoverContent>
        }
      >
        <span role='popover-expand' aria-label='Agent Ellipsis'>
          <Icon
            role='icon-button'
            aria-label='Agent Ellipsis'
            iconName='more-2'
            title='Click the action menu to access computer specific options.'
          />
        </span>
      </Popover>
    </div>
  );
});

export const SubRowRenderer = (props: GridRendererProps<AgentData>): React.JSX.Element => {
  const { row } = props;
  return (
    <div className='subrow-qualifications'>
      <div className='subrow-header'>
        QUALIFICATIONS{' '}
        <Link
          onClick={() =>
            window.open('https://www.idexx.com/files/cornerstone-hw-os-guidelines.pdf')
          }
        >
          Hardware &amp; System guidelines
        </Link>
      </div>
      <div className='subrow-content'>
        <div className='section-title'>CORNERSTONE REQUIREMENTS</div>
        <div className='flex-box'>
          <ul>
            <li>
              <QualificationIcon pass={row.qualifications?.os} /> Operating System:{' '}
              <strong>{row.os}</strong>
            </li>
            {/^10 /.test(row.os) && (
              <li>
                <QualificationIcon
                  pass={row.win11Readiness}
                  failTitle='Windows 11 readiness is shown for reference only. Windows 10 computers remain eligible for upgrade.'
                  warn
                />{' '}
                Windows 11 Readiness:{' '}
                <strong>
                  {!isDefined(row.win11Readiness)
                    ? 'Unknown'
                    : row.win11Readiness
                      ? 'Ready'
                      : 'Not Ready'}
                </strong>
              </li>
            )}
            <li>
              <QualificationIcon pass={row.qualifications?.processor} /> Processor:{' '}
              <strong>{row.processor}</strong>
            </li>
            <li>
              <QualificationIcon pass={row.qualifications?.memory} /> Memory:{' '}
              <strong>{formatMBytes(row.memoryMb)}</strong>
            </li>
          </ul>
          <ul>
            <li>
              <QualificationIcon pass={row.qualifications?.csVersion} /> Cornerstone:{' '}
              <strong>{row.csVersion}</strong>
            </li>
            <li>
              <QualificationIcon pass={row.qualifications?.csDrive} /> Cornerstone Drive:{' '}
              <strong>{row.csDrive}</strong>
              {!row.csDrive && (
                <Icon
                  className='info-icon'
                  iconName='info-2'
                  title='We cannot determine where Cornerstone is installed on your computer.'
                />
              )}
            </li>
            <li>
              <QualificationIcon pass={row.qualifications?.csDiskSize} /> Cornerstone Drive Disk
              Capacity: <strong>{formatMBytes(row.csDiskSizeMb)}</strong>
              <Icon
                className='info-icon'
                iconName='info-2'
                title='This is an approximate amount of usable space and may not match the true drive size'
              />
            </li>
          </ul>
          <ul>
            <li>
              <QualificationIcon pass={row.qualifications?.cDiskFreeSpace} /> Free Disk Space On C:\
              Drive: <strong>{formatMBytes(row.cDiskFreeSpaceMb)}</strong>
              <Icon
                className='info-icon'
                iconName='info-2'
                title={
                  row.qualifications?.cDiskFreeSpace
                    ? 'The upgrade process downloads files and makes backups of your database to the C:\\ drive, regardless of which drive Cornerstone is installed on.'
                    : 'We cannot determine if there is enough space to back up critical files during your upgrade.'
                }
              />
            </li>
          </ul>
        </div>
        <div className='section-title'>UPGRADE AUTOMATION REQUIREMENTS</div>
        <div className='flex-box'>
          <ul>
            <li>
              <QualificationIcon pass={row.qualifications?.connectivity} /> Upgrade System
              Connectivity
              <Icon
                className='info-icon'
                iconName='info-2'
                title='Ability to download upgrade files'
              />
            </li>
          </ul>
          <ul>
            <li>
              <QualificationIcon pass={row.qualifications?.powershell} /> Powershell Script
              Execution
              <Icon
                className='info-icon'
                iconName='info-2'
                title='Ability to execute upgrade PowerShell scripts'
              />
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export const FailedQualificationsModalContent = ({
  qualifications,
}: {
  qualifications: AgentQualifications;
}): React.JSX.Element => (
  <div className='failed-qualifications-modal-content'>
    <p>
      This computer is not qualified for automated upgrade. See below for an explanation of what
      MyCornerstone is evaluating and recommendations for resolution.
    </p>
    <ul>
      {!qualifications.os && (
        <li>
          <strong>Operating System</strong> - Your computer&apos;s operating system is not
          supported. Consider replacing the computer, or if possible upgrading to a supported
          version of Windows as seen in the Hardware & System guidelines.
        </li>
      )}
      {!qualifications.processor && (
        <li>
          <strong>Processor</strong> - Your computer&apos;s processor is not supported. IDEXX
          recommends an Intel Core CPU. MyCornerstone also considers certain AMD processor families
          as qualified which are not included on published IDEXX recommendations. Consider replacing
          the computer.
        </li>
      )}
      {!qualifications.memory && (
        <li>
          <strong>Memory</strong> - Your computer does not have sufficient memory (RAM) installed.
          Consider replacing the computer, or if possible installing additional memory to align with
          requirements found in the Hardware & System guidelines.
        </li>
      )}
      {!qualifications.csVersion && (
        <li>
          <strong>Cornerstone</strong> - MyCornerstone cannot be used to upgrade from your current
          version of Cornerstone. Upgrade manually to at least Cornerstone 9.1 before scheduling an
          upgrade with MyCornerstone.
        </li>
      )}
      {!qualifications.csDiskSize && (
        <li>
          <strong>Disk Capacity</strong> - Your computer does not meet IDEXX&apos;s recommendations
          for hard drive capacity. MyCornerstone allows for some discrepancy between
          manufacturer&apos;s published disk size and true disk capacity. Consider replacing the
          computer, or if possible installing a hard drive with greater capacity which aligns with
          requirements found in the Hardware & System guidelines.
        </li>
      )}
      {!qualifications.cDiskFreeSpace && (
        <li>
          <strong>Free Disk Space</strong> - Your computer does not have enough free space to
          support the upgrade. Workstations require at least 4GB of free space, while servers
          require 4GB plus additional space to make copies of critical database and log files. Note
          that for servers, regardless of which drive Cornerstone is installed on, these backups are
          always made on the C:\ drive. Consider deleting unnecessary files to create more free
          space or installing a hard drive with greater capacity.
        </li>
      )}
      {!qualifications.connectivity && (
        <li>
          <strong>Upgrade Systems Connectivity</strong> - Your computer cannot connect to the remote
          systems used to manage the upgrade. Ensure that the computer is connected to the internet,
          and review antivirus and firewall configuration.
        </li>
      )}
      {!qualifications.powershell && (
        <li>
          <strong>Powershell</strong> - MyCornerstone cannot use Windows PowerShell to perform
          upgrade actions. Ensure that PowerShell is installed, up to date, and that there are no
          local policies which may prevent scripts from being executed by the SYSTEM user.
        </li>
      )}
    </ul>
  </div>
);

export const MessageMultipleServersDetected = (): React.JSX.Element => (
  <>
    It looks like more than one computer was configured as a Cornerstone server recently.
    <br />
    Please contact Cornerstone support at <strong>1-800-695-2877</strong> to identify your true
    server before continuing.
  </>
);

export const MessageNoServerDetected = (): React.JSX.Element => (
  <>
    It looks like we cannot identify your Cornerstone Server.
    <br />
    Please contact Cornerstone support at <strong>1-800-695-2877</strong> to identify your server
    before continuing.
  </>
);

export const MessageRemoveOverride = (): React.JSX.Element => (
  <>
    The manual approval to upgrade this computer will be removed.
    <br /> Do you want to continue?
  </>
);

export const MessageNeedHelp = (): React.JSX.Element => (
  <ul>
    <li>
      Step 1 - Create nicknames for easy machine recognition. Use <strong>Find me</strong> to
      identify machines.
    </li>
    <li>Step 2 - Select computers to include with automatic upgrade.</li>
    <li>Step 3 - Initiate Check and review qualifications</li>
    <li>Step 4 - Confirm Schedule</li>
  </ul>
);

export const MessageSendInstructionsSent = (userEmail: string) => (): React.JSX.Element => (
  <>
    An email with the download link and installation instructions has been sent to{' '}
    <strong>{userEmail}</strong>
  </>
);

export const MessageSendInstructionsFailed = (): React.JSX.Element => (
  <>
    <p>We are unable to send the email at this time.</p>
    <p>Please use the download link and on screen instructions or try again later.</p>
  </>
);

export const MessageAdditionalSetupRequired = (): React.JSX.Element => (
  <>
    <p>
      IDEXX needs to make some changes to your practice&apos;s account before you can schedule your
      upgrade.
    </p>
    <p>Please allow up to 4 business days for this process to complete and try again.</p>
  </>
);

export const MessageInsufficientDiskCSpace =
  (serverName: any, requiredFreeSpace: any) => (): React.JSX.Element => (
    <>
      <p>
        During the upgrade process, Cornerstone makes backups of critical files to{' '}
        <strong>{serverName}</strong>&apos;s C:\ drive.
      </p>
      <p>
        In order to successfully complete the upgrade, you must have at least{' '}
        <strong>{requiredFreeSpace}</strong> of disk space available.
      </p>
    </>
  );

export const MessageDuplicateComputerNames = (): React.JSX.Element => (
  <>
    <p>More than one computer you want to upgrade share the same name.</p>
    <p>
      Make changes to computer names at your practice and then perform another qualification check
      to update MyCornerstone.
    </p>
  </>
);

export const MessageOverrideFailedQualifications = (): React.JSX.Element => (
  <>
    <p>
      This computer does not meet IDEXX&apos;s Hardware &amp; Operating System guidelines for the
      latest Cornerstone version. If upgraded, performance of Cornerstone may be adversely affected.
    </p>
    <p>Do you wish to continue?</p>
  </>
);

export const expandCollapseRenderer =
  (action: (row: AgentData) => void): GridRenderer<AgentData> =>
  (props: GridRendererProps<AgentData>) => (
    <ExpandCollapseRenderer
      row={props.row}
      index={props.index}
      expandedRowIds={props.expandedRowIds}
      action={action}
    />
  );
